import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Map, MapBrowserEvent, View } from 'ol';
import { EventsKey } from 'ol/events';
import { ObjectEvent } from 'ol/Object';
import { unByKey } from 'ol/Observable';
import { Coordinate } from 'ol/coordinate';
import { Role, ViewMode } from 'types/app.types';
import { createLogger } from 'services/debugLogger';
import TileViewContext from '../../TileViewContext';
import useGenerateMap from './useGenerateMap';
import usePopup from './usePopup';
import useBaseLayers from './useBaseLayers';
import useADSBLayer from './useADSBLayer';
import useTimeSeriesPlayer from './useTimeSeriesPlayer';
import useProductLayers from './useProductLayers';
import ProductsContext from '../../ProductsContext';
import useLineStringInteraction from './useLineStringInteraction';
import MainAppContext from '../../MainAppContext';
import LayerViewContext from '../../LayerViewContext';
import useConnectedLayers from './useConnectedLayers';
import { utcDayjs } from '../../../utils';
import { useGlobalMapCommands } from '../../GlobalMapCommandsContext';

const logger = createLogger('useMap');

const useMap = (index : number, viewMode: ViewMode) => {
  const captureZoomEventHandler = useRef<EventsKey|undefined>();
  const captureOnHoverEventHandler = useRef<EventsKey|undefined>();
  const baseLayerUploadingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    playbackMode, syncLayers, latestView, role, setSyncLayers,
  } = useContext(MainAppContext);
  const [loadingLayer, setLoadingLayer] = useState(false);
  const {
    captureZoomEvent,
    captureOnHoverEvent: captureTileOnHoverEvent,
    zoomEvent,
    onHoverEvent: tileOnHoverEvent,
  } = useContext(TileViewContext);
  const { captureOnHoverEvent: captureLayerOnHoverEvent } = useContext(LayerViewContext);
  const { activeProducts, selectedProducts } = useContext(ProductsContext);
  const { currentCommand, clearCommand } = useGlobalMapCommands();

  const { map, mapContainer } = useGenerateMap();
  const { updateBaseLayers } = useBaseLayers(map);
  const {
    loadingAnimationCharge,
    cancelPrechargeAnimation,
    preloadLayer,
    preloadAllLayers,
  } = useTimeSeriesPlayer(map);
  const {
    updateLayerStyle,
    updateLayerOpacity,
    updateLayerElevation,
    addLayer,
    removeLayer,
    moveLayer,
    replaceLayer,
    getLayerTooltip,
    playbackTooltipEnabled,
  } = useProductLayers({
    map,
    index,
    viewMode,
    setLoadingLayer,
    preloadLayerFn: preloadLayer,
  });
  const { popupContainer, popupInfo } = usePopup(
    map,
    index,
    getLayerTooltip,
    playbackTooltipEnabled,
  );

  useLineStringInteraction(map);
  useADSBLayer(map);
  useConnectedLayers(map);

  // Handle global commands for preloading layers
  useEffect(() => {
    if (currentCommand?.command === 'preloadAllLayers' && currentCommand.payload) {
      logger.info(`MapView${index}: Executing global preloadAllLayers command`, currentCommand);
      preloadAllLayers(currentCommand.payload)
        .then(() => {
          logger.info(`MapView${index}: preloadAllLayers completed`);
        })
        .catch((error) => {
          logger.error(`MapView${index}: preloadAllLayers failed`, error);
        })
        .finally(() => {
          // Clear the command to avoid re-execution
          clearCommand();
        });
    }
  }, [currentCommand, index, preloadAllLayers, clearCommand]);

  // If an onHover event has been triggered and sync-layers mode is activated,
  // an onHover event will be triggered on this map.
  // This is mostly for the cursor replication in tiled-sync mode.
  // The onHoverEvent is fed by a single map, and replicated to the rest to the displayed maps.
  useEffect(() => {
    if (tileOnHoverEvent?.event && syncLayers) {
      map?.dispatchEvent(tileOnHoverEvent.event);
    }
  }, [map, tileOnHoverEvent?.event, syncLayers]);

  useEffect(() => {
    if (syncLayers) {
      map.getView().dispatchEvent('change:resolution');
    }
  }, [map, syncLayers]);

  // This handles sync layers changes and the view of the map
  // When syncLayers, the View is shared between all the displayed maps
  // When syncLayers is not active,
  // a new View is generated for every map and zoom and center are set manually
  useEffect(() => {
    if (syncLayers && latestView.current[0]) {
      map.setView(latestView.current[0]);
    }

    if (!syncLayers && latestView.current[0]) {
      const view = latestView.current[0];

      map.setView(
        new View({
          zoom: view?.getZoom(),
          center: view?.getCenter(),
        }),
      );
    }
  }, [index, latestView, map, syncLayers]);

  useEffect(() => {
    if (viewMode === 'tile' && selectedProducts && latestView.current[0]) {
      map.setView(latestView.current[0]);
    }
  }, [latestView, map, selectedProducts, viewMode]);

  // This handles the pointermove event of the map
  // This event is stored globally (to feed other maps -in sync mode- or the popup feature).
  // Moreover is also stored the current View to configure maps when the user switchs between modes.
  useEffect(() => {
    captureOnHoverEventHandler.current = map.on(
      'pointermove',
      (event: MapBrowserEvent<PointerEvent>) => {
        captureTileOnHoverEvent({
          event,
          center: (event?.target as Map)?.getView().getCenter() as Coordinate,
        });
        captureLayerOnHoverEvent({
          event,
          center: (event?.target as Map)?.getView().getCenter() as Coordinate,
        });
        latestView.current[index] = event.target.getView();
      },
    );

    return () => {
      if (captureOnHoverEventHandler.current) {
        unByKey(captureOnHoverEventHandler.current);
        captureOnHoverEventHandler.current = undefined;
      }
    };
  }, [
    captureLayerOnHoverEvent,
    captureTileOnHoverEvent,
    index,
    latestView,
    map,
  ]);

  // When syncLayers, force a updateBaseLayers event when a zoom event is triggered.
  // The base layers change depending on the zoom applied to them.
  useEffect(() => {
    if (syncLayers && zoomEvent) {
      if (baseLayerUploadingTimer.current) {
        clearTimeout(baseLayerUploadingTimer.current);
      }

      baseLayerUploadingTimer.current = setTimeout(
        () => updateBaseLayers(),
        300,
      );
    }
  }, [syncLayers, updateBaseLayers, zoomEvent]);

  // A pending base-layer refresh must not fire after the map has been unmounted.
  useEffect(() => () => {
    if (baseLayerUploadingTimer.current) {
      clearTimeout(baseLayerUploadingTimer.current);
      baseLayerUploadingTimer.current = null;
    }
  }, []);

  // This handles the zoom (change:resolution) event of the map
  // This event is stored globally (to feed other maps -in sync mode- or the popup feature).
  // Moreover is also stored the current View to configure maps when the user switchs between modes.
  useEffect(() => {
    captureZoomEventHandler.current = map
      ?.getView()
      .on('change:resolution', (event: ObjectEvent) => {
        captureZoomEvent(event);
        latestView.current[index] = event.target;

        if (baseLayerUploadingTimer.current) {
          clearTimeout(baseLayerUploadingTimer.current);
        }
        baseLayerUploadingTimer.current = setTimeout(
          () => updateBaseLayers(),
          300,
        );
      });

    return () => {
      if (captureZoomEventHandler.current) {
        unByKey(captureZoomEventHandler.current);
        captureZoomEventHandler.current = undefined;
      }
    };
  }, [captureZoomEvent, index, latestView, map, updateBaseLayers]);

  // Handles loadstart and loadend events of the map.
  // Mostly to show/hide loaders
  useEffect(() => {
    const loadStart = map?.on('loadstart', (e) => {
      if (!playbackMode) {
        setLoadingLayer(true);
      }
    });
    const loadEnd = map?.on('loadend', (e) => {
      if (!playbackMode) {
        setLoadingLayer(false);
      }
    });

    return () => {
      unByKey(loadStart);
      unByKey(loadEnd);
    };
  }, [map, playbackMode]);

  // Global effects to update BaseLayers when there is some change in activeProducts array
  useEffect(() => {
    if (activeProducts) {
      updateBaseLayers();
    }
  }, [activeProducts, map, updateBaseLayers]);

  useEffect(() => {
    if (role === Role.SUPERVISOR && map && selectedProducts.length !== 4) {
      selectedProducts.forEach((product) => {
        addLayer(product, utcDayjs().toISOString());
      });
      setSyncLayers(false);
      setSyncLayers(true);
    }
  }, [addLayer, role, selectedProducts, map, latestView, setSyncLayers]);

  useEffect(() => {
    if (role === Role.SUPERVISOR && map && selectedProducts.length === 4) {
      setSyncLayers(false);
      setSyncLayers(true);
    }
  }, [map, role, selectedProducts.length, setSyncLayers]);
  return {
    map,
    mapContainer,
    popupContainer,
    loadingLayer,
    loadingAnimationCharge,
    popupInfo,
    updateLayerStyle,
    updateLayerOpacity,
    updateLayerElevation,
    updateGeoLayer: () => {}, // Default implementation
    addLayer,
    removeLayer,
    prechargeAnimation: async () => Promise.resolve(), // Default implementation
    cancelPrechargeAnimation,
    moveLayer,
    replaceLayer,
    preloadLayer,
    preloadAllLayers,
  };
};

export default useMap;
